import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  Globe,
  TrendingUp,
  Package,
  Truck,
  Download,
  Calendar,
  Users,
  DollarSign,
  MapPin,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Coins
} from 'lucide-react';
import { generateGlobalReport, exportReportToCSV } from '../services/reportsService';
import { GlobalReport, ReportFilters } from '../types/reports';
import { COUNTRIES } from '../data/countries';
import useTranslate from '../hooks/useTranslate';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { Currency } from '../types';

interface AdminReportsProps {
  onClose?: () => void;
}

const AdminReports: React.FC<AdminReportsProps> = ({ onClose }) => {
  const [report, setReport] = useState<GlobalReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'7days' | '30days' | '90days' | 'all'>('30days');
  const [expandedCountry, setExpandedCountry] = useState<string | null>(null);
  const { translateVehicleType, translateCargoType } = useTranslate();
  const { t, i18n } = useTranslation();

  const loadReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const filters: ReportFilters = {
        startDate: getStartDate(dateRange),
        endDate: new Date()
      };

      const reportData = await generateGlobalReport(filters);
      setReport(reportData);
    } catch (err: any) {
      console.error('Error loading report:', err);
      setError(t('admin.reports.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [dateRange]);

  const getStartDate = (range: string): Date => {
    const now = new Date();
    switch (range) {
      case '7days':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case '30days':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case '90days':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      case 'all':
      default:
        return new Date(0);
    }
  };

  const handleExportCSV = () => {
    if (!report) return;

    const csv = exportReportToCSV(report, i18n.language);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const filename = t('admin.reports.export.filename');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleCountryExpansion = (countryCode: string) => {
    setExpandedCountry(prev => prev === countryCode ? null : countryCode);
  };

  const getMostCommonCurrency = (): string => {
    if (!report) return 'MXN';

    const currencyCount: Record<string, number> = {};
    report.topRoutes.forEach(route => {
      if (route.currencyCount) {
        Object.entries(route.currencyCount).forEach(([currency, count]) => {
          currencyCount[currency] = (currencyCount[currency] || 0) + count;
        });
      }
    });

    let mostCommon = 'MXN';
    let maxCount = 0;
    Object.entries(currencyCount).forEach(([currency, count]) => {
      if (count > maxCount) {
        maxCount = count;
        mostCommon = currency;
      }
    });

    return mostCommon;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="p-4 sm:p-6 bg-gradient-to-r from-blue-400 to-blue-500 text-white">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold flex items-center">
            <BarChart3 className="mr-2" size={28} />
            {t('admin.reports.title')}
          </h2>
          <button
            onClick={loadReport}
            className="p-2 rounded-full hover:bg-blue-500 transition-colors"
          >
            <RefreshCw size={20} />
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setDateRange('7days')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              dateRange === '7days'
                ? 'bg-white text-blue-700 font-semibold'
                : 'bg-blue-500 hover:bg-blue-400 text-white'
            }`}
          >
            <Calendar size={16} className="inline mr-1" />
            {t('admin.reports.dateRanges.last7Days')}
          </button>
          <button
            onClick={() => setDateRange('30days')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              dateRange === '30days'
                ? 'bg-white text-blue-700 font-semibold'
                : 'bg-blue-500 hover:bg-blue-400 text-white'
            }`}
          >
            <Calendar size={16} className="inline mr-1" />
            {t('admin.reports.dateRanges.last30Days')}
          </button>
          <button
            onClick={() => setDateRange('90days')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              dateRange === '90days'
                ? 'bg-white text-blue-700 font-semibold'
                : 'bg-blue-500 hover:bg-blue-400 text-white'
            }`}
          >
            <Calendar size={16} className="inline mr-1" />
            {t('admin.reports.dateRanges.last90Days')}
          </button>
          <button
            onClick={() => setDateRange('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              dateRange === 'all'
                ? 'bg-white text-blue-700 font-semibold'
                : 'bg-blue-500 hover:bg-blue-400 text-white'
            }`}
          >
            <Calendar size={16} className="inline mr-1" />
            {t('admin.reports.dateRanges.allTime')}
          </button>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 p-6 rounded-xl border-2 border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-blue-700 uppercase tracking-wide">{t('admin.reports.summary.totalQuotations')}</h3>
              <div className="bg-blue-600 p-2.5 rounded-lg shadow-md">
                <BarChart3 size={22} className="text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-blue-900 mb-1">{report.summary.totalQuotations}</p>
            <div className="h-1 w-16 bg-blue-600 rounded-full mt-2"></div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-50 p-6 rounded-xl border-2 border-emerald-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">{t('admin.reports.summary.uniqueUsers')}</h3>
              <div className="bg-emerald-600 p-2.5 rounded-lg shadow-md">
                <Users size={22} className="text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-emerald-900 mb-1">{report.summary.uniqueUsers}</p>
            <div className="h-1 w-16 bg-emerald-600 rounded-full mt-2"></div>
          </div>

          <div className="bg-gradient-to-br from-cyan-50 via-cyan-100 to-cyan-50 p-6 rounded-xl border-2 border-cyan-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-cyan-700 uppercase tracking-wide">{t('admin.reports.summary.totalDistance')}</h3>
              <div className="bg-cyan-600 p-2.5 rounded-lg shadow-md">
                <MapPin size={22} className="text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-cyan-900 mb-1">
              {report.summary.totalDistance.toLocaleString(i18n.language === 'es' ? 'es-MX' : 'en-US', { maximumFractionDigits: 0 })} km
            </p>
            <div className="h-1 w-16 bg-cyan-600 rounded-full mt-2"></div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 via-orange-100 to-orange-50 p-6 rounded-xl border-2 border-orange-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-orange-700 uppercase tracking-wide">{t('admin.reports.summary.averageCost')}</h3>
              <div className="bg-orange-600 p-2.5 rounded-lg shadow-md">
                <DollarSign size={22} className="text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-orange-900 mb-1">
              {getCurrencySymbol(getMostCommonCurrency() as Currency)}{report.summary.averageCostPerQuotation.toLocaleString(i18n.language === 'es' ? 'es-MX' : 'en-US', { maximumFractionDigits: 0 })}
            </p>
            <div className="flex items-center mt-2">
              <Coins size={14} className="text-orange-600 mr-1" />
              <span className="text-xs font-medium text-orange-700">{getMostCommonCurrency()}</span>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-800 flex items-center">
              <Globe className="mr-2 text-blue-600" size={24} />
              {t('admin.reports.countryDestinations.title')}
            </h3>
            <button
              onClick={handleExportCSV}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download size={16} className="mr-2" />
              {t('admin.reports.export.button')}
            </button>
          </div>

          {report.countryDestinations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('admin.reports.countryDestinations.noData')}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.reports.countryDestinations.tableHeaders.country')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.reports.countryDestinations.tableHeaders.quotations')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.reports.countryDestinations.tableHeaders.percentage')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.reports.countryDestinations.tableHeaders.cities')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {report.countryDestinations.map((country) => {
                    const countryData = COUNTRIES.find(c => c.code === country.countryCode);
                    const isExpanded = expandedCountry === country.countryCode;

                    return (
                      <React.Fragment key={country.countryCode}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-2xl mr-2">{countryData?.flag}</span>
                              <span className="font-medium text-gray-900">{country.countryName}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-lg font-semibold text-blue-600">
                              {country.quotationCount}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-32 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${Math.min(country.percentage, 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-700">
                                {country.percentage.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {country.cities.length > 0 && (
                              <button
                                onClick={() => toggleCountryExpansion(country.countryCode)}
                                className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                              >
                                {isExpanded ? (
                                  <>
                                    <ChevronUp size={16} className="mr-1" />
                                    {t('admin.reports.countryDestinations.hideCities')}
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown size={16} className="mr-1" />
                                    {t('admin.reports.countryDestinations.showCities', { count: country.cities.length })}
                                  </>
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && country.cities.length > 0 && (
                          <tr>
                            <td colSpan={4} className="px-6 py-4 bg-gray-50">
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                {country.cities.map((city, idx) => (
                                  <div
                                    key={idx}
                                    className="flex justify-between items-center bg-white px-3 py-2 rounded border border-gray-200"
                                  >
                                    <span className="text-sm text-gray-700">{city.cityName}</span>
                                    <span className="text-sm font-semibold text-blue-600">
                                      {city.quotationCount}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <TrendingUp className="mr-2 text-green-600" size={24} />
            {t('admin.reports.topRoutes.title')}
          </h3>

          {report.topRoutes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('admin.reports.topRoutes.noData')}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      {t('admin.reports.topRoutes.tableHeaders.origin')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      {t('admin.reports.topRoutes.tableHeaders.destination')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      {t('admin.reports.topRoutes.tableHeaders.quotations')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      {t('admin.reports.topRoutes.tableHeaders.avgDistance')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      {t('admin.reports.topRoutes.tableHeaders.avgCost')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center">
                        <Coins size={14} className="mr-1" />
                        {i18n.language === 'es' ? 'Moneda' : 'Currency'}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {report.topRoutes.slice(0, 10).map((route, idx) => (
                    <tr key={idx} className="hover:bg-gradient-to-r hover:from-green-50 hover:to-transparent transition-colors duration-150">
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate font-medium">
                        {route.origin}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate font-medium">
                        {route.destination}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                          {route.quotationCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                        {route.averageDistance.toFixed(0)} km
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {getCurrencySymbol(route.currency as Currency)}{route.averageCost.toLocaleString(i18n.language === 'es' ? 'es-MX' : 'en-US', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold bg-orange-100 text-orange-800 border border-orange-200">
                          <Coins size={12} className="mr-1" />
                          {route.currency}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 border-t border-gray-200 pt-6">
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <Truck className="mr-2 text-blue-600" size={24} />
              {t('admin.reports.vehicleTypes.title')}
            </h3>

            {report.vehicleTypeStats.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('admin.reports.vehicleTypes.noData')}
              </div>
            ) : (
              <div className="space-y-2">
                {report.vehicleTypeStats.slice(0, 5).map((stat, idx) => (
                  <div key={idx} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        {translateVehicleType(stat.vehicleType)}
                      </span>
                      <span className="text-sm font-semibold text-blue-600">
                        {stat.count} ({stat.percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${Math.min(stat.percentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <Package className="mr-2 text-purple-600" size={24} />
              {t('admin.reports.cargoTypes.title')}
            </h3>

            {report.cargoTypeStats.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('admin.reports.cargoTypes.noData')}
              </div>
            ) : (
              <div className="space-y-2">
                {report.cargoTypeStats.slice(0, 5).map((stat, idx) => (
                  <div key={idx} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        {translateCargoType(stat.cargoType)}
                      </span>
                      <span className="text-sm font-semibold text-purple-600">
                        {stat.count} ({stat.percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{ width: `${Math.min(stat.percentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;
